import { Component, useEffect, useMemo, useRef, useState } from "react";
import type { ErrorInfo, ReactNode } from "react";
import { Routes, Route, useParams, useSearchParams } from "react-router-dom";
import { fetchResult, pollKeyFeaturesIfNeeded } from "./api/client";
import { LabelsProvider, type WidgetLabels } from "./lib/labels";
import { postToHost, reportSize } from "./lib/postmessage";
import type { ResultEnvelope } from "./types";

class WidgetErrorBoundary extends Component<
  { children: ReactNode },
  { error: Error | null }
> {
  state: { error: Error | null } = { error: null };

  static getDerivedStateFromError(error: Error) {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    console.error("[Widget] Card render error:", error, info.componentStack);
  }

  render() {
    if (this.state.error) {
      return (
        <div className="ac-error-boundary">
          <strong className="ac-error-boundary__title">Rendering failed</strong>
          <span className="ac-error-boundary__msg">{this.state.error.message}</span>
        </div>
      );
    }
    return this.props.children;
  }
}

import ProductDetailCard from "./cards/ProductDetailCard";
import BundleComponentsCard from "./cards/BundleComponentsCard";
import PromotionListCard from "./cards/PromotionListCard";
import ReviewSummaryCard from "./cards/ReviewSummaryCard";
import CartAddedCard from "./cards/CartAddedCard";
import CouponListCard from "./cards/CouponListCard";
import OrderListCard from "./cards/OrderListCard";
import LoginFormCard from "./cards/LoginFormCard";
import EmailFormCard from "./cards/EmailFormCard";
import CouponInputCard from "./cards/CouponInputCard";
import PostcodeInputCard from "./cards/PostcodeInputCard";
import ConfirmationCard from "./cards/ConfirmationCard";
import SpecTableCard from "./cards/SpecTableCard";
import AiTopPicksCard from "./cards/AiTopPicksCard";
import CompareTableCard from "./cards/CompareTableCard";
import ConceptCompareTableCard from "./cards/ConceptCompareTableCard";
import CarePlanCard from "./cards/CarePlanCard";
import DeliveryComparisonTableCard from "./cards/DeliveryComparisonTableCard";
import SecretCouponCard from "./cards/SecretCouponCard";
import PromotionProductsCard from "./cards/PromotionProductsCard";
import TrackMyOrdersFormCard from "./cards/TrackMyOrdersFormCard";
import SimpleProductCard from "./cards/SimpleProductCard";
import CtaButtonCard from "./cards/CtaButtonCard";
import FindMyModelCard from "./cards/FindMyModelCard";
import LiveChatCtaCard from "./cards/LiveChatCtaCard";
import ChatSummaryNudgeCard from "./cards/ChatSummaryNudgeCard";
import LoginNudgeCard from "./cards/LoginNudgeCard";
import EvidenceReportCard from "./cards/EvidenceReportCard";
import { WidgetShowcase } from "./WidgetShowcase";

function WidgetView() {
  const { component: paramComponent } = useParams<{ component: string }>();
  const [params] = useSearchParams();
  const sessionToken = params.get("sessionToken") || "";
  const resultId = params.get("resultId") || "";
  const queryComponent = params.get("component") || paramComponent || "";

  const [env, setEnv] = useState<ResultEnvelope | null>(null);
  const [error, setError] = useState<string | null>(null);
  const rootRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!resultId) {
      setError("missing resultId");
      return;
    }
    let cancelled = false;
    setError(null);
    setEnv(null);

    fetchResult(resultId, sessionToken)
      .then((initial) => {
        if (cancelled) return;
        setEnv(initial);
        void pollKeyFeaturesIfNeeded(
          resultId,
          sessionToken,
          initial,
          (updated) => {
            if (!cancelled) setEnv(updated);
          },
          () => cancelled,
        );
      })
      .catch((e) => {
        if (!cancelled) setError(String(e));
      });

    return () => {
      cancelled = true;
    };
  }, [resultId, sessionToken]);

  useEffect(() => {
    postToHost("widget.ready", { component: queryComponent }, { resultId, sessionId: sessionToken });
  }, [queryComponent, resultId, sessionToken]);

  useEffect(() => {
    if (!rootRef.current) return;
    return reportSize(rootRef.current, { resultId, sessionId: sessionToken });
  }, [env, resultId, sessionToken]);

  const componentName = useMemo(
    () => env?.component || queryComponent,
    [env, queryComponent]
  );

  let body: React.ReactNode;
  if (error) {
    body = <ErrorBanner message={error} />;
  } else if (!env) {
    body = <Skeleton />;
  } else {
    const labels = (env.data as { labels?: WidgetLabels })?.labels;
    body = (
      <WidgetErrorBoundary>
        <LabelsProvider labels={labels}>
          {renderCard(componentName, env, sessionToken)}
        </LabelsProvider>
      </WidgetErrorBoundary>
    );
  }

  const isCompare =
    componentName === "compare_table" || componentName === "concept_compare_table";
  const isOrderWidget = ["order_list", "track_my_orders_form"].includes(componentName);

  const shellClass = isCompare
    ? "ac-shell--white"
    : isOrderWidget
      ? "ac-shell--plain"
      : "ac-shell";

  // hyphens:auto(독일어 합성어 음절 분리)가 동작하려면 조상에 lang 이 필요하다.
  const lang = (env?.data as { languageCode?: string } | undefined)?.languageCode;

  return (
    <div ref={rootRef} className={shellClass} lang={lang || undefined}>
      {body}
    </div>
  );
}

export function renderCard(
  component: string,
  env: ResultEnvelope,
  sessionToken: string
): React.ReactNode {
  const data = env.data as never;
  switch (component) {
    case "product_detail":
      return <ProductDetailCard data={data} sessionToken={sessionToken} />;
    case "bundle_components":
      return <BundleComponentsCard data={data} sessionToken={sessionToken} />;
    case "promotion_list":
      return <PromotionListCard data={data} sessionToken={sessionToken} />;
    case "review_summary":
      return <ReviewSummaryCard data={data} sessionToken={sessionToken} />;
    case "cart_added":
      return <CartAddedCard data={data} sessionToken={sessionToken} />;
    case "coupon_list":
      return <CouponListCard data={data} sessionToken={sessionToken} />;
    case "my_coupon_list":
      return <CouponListCard data={data} sessionToken={sessionToken} variant="myCoupon" />;
    case "secret_coupon":
      return <SecretCouponCard data={data} sessionToken={sessionToken} />;
    case "order_list":
      return <OrderListCard data={data} sessionToken={sessionToken} />;
    case "login_form":
      return <LoginFormCard data={data} sessionToken={sessionToken} />;
    case "email_form":
      return <EmailFormCard data={data} sessionToken={sessionToken} />;
    case "coupon_input":
      return <CouponInputCard data={data} sessionToken={sessionToken} />;
    case "postcode_input":
      return <PostcodeInputCard data={data} sessionToken={sessionToken} />;
    case "confirmation":
      return <ConfirmationCard data={data} sessionToken={sessionToken} />;
    case "spec_table":
    case "spec_comparison_table":
      return <SpecTableCard data={data} sessionToken={sessionToken} />;
    case "ai_top_picks":
      return <AiTopPicksCard data={data} sessionToken={sessionToken} />;
    case "compare_table":
      return <CompareTableCard data={data} sessionToken={sessionToken} />;
    case "concept_compare_table":
      return <ConceptCompareTableCard data={data} sessionToken={sessionToken} />;
    case "delivery_comparison_table":
      return <DeliveryComparisonTableCard data={data} sessionToken={sessionToken} />;
    case "care_plan_card":
      return <CarePlanCard data={data} sessionToken={sessionToken} />;
    case "promotion_products":
      return <PromotionProductsCard data={data} sessionToken={sessionToken} />;
    case "track_my_orders_form":
      return <TrackMyOrdersFormCard data={data} sessionToken={sessionToken} />;
    case "cart_confirm_preview":
    case "simple_product_carousel":
      return <SimpleProductCard data={data} sessionToken={sessionToken} />;
    case "cta_button":
      return <CtaButtonCard data={data} sessionToken={sessionToken} />;
    case "find_my_model":
      return <FindMyModelCard data={data} sessionToken={sessionToken} />;
    case "live_chat_cta":
      return <LiveChatCtaCard data={data} sessionToken={sessionToken} />;
    case "chat_summary_nudge":
      return <ChatSummaryNudgeCard data={data} sessionToken={sessionToken} />;
    case "login_nudge":
      return <LoginNudgeCard data={data} sessionToken={sessionToken} />;
    case "evidence_report":
      return <EvidenceReportCard data={data} sessionToken={sessionToken} />;
    default:
      return <UnknownComponent component={component} env={env} />;
  }
}

export function hasCardRenderer(component: string): boolean {
  switch (component) {
    case "product_detail":
    case "bundle_components":
    case "promotion_list":
    case "review_summary":
    case "cart_added":
    case "coupon_list":
    case "my_coupon_list":
    case "secret_coupon":
    case "order_list":
    case "login_form":
    case "email_form":
    case "coupon_input":
    case "postcode_input":
    case "confirmation":
    case "spec_table":
    case "spec_comparison_table":
    case "ai_top_picks":
    case "compare_table":
    case "concept_compare_table":
    case "delivery_comparison_table":
    case "care_plan_card":
    case "promotion_products":
    case "track_my_orders_form":
    case "cart_confirm_preview":
    case "simple_product_carousel":
    case "cta_button":
    case "live_chat_cta":
    case "chat_summary_nudge":
    case "login_nudge":
    case "evidence_report":
    case "find_my_model":
      return true;
    default:
      return false;
  }
}

function UnknownComponent({
  component,
  env,
}: {
  component: string;
  env: ResultEnvelope;
}) {
  return (
    <div className="ac-unknown">
      <code className="ac-unknown__code">{component || "(unknown)"}</code>
      <pre className="ac-unknown__json">{JSON.stringify(env.data, null, 2)}</pre>
    </div>
  );
}

function Skeleton() {
  return (
    <div className="ac-skel">
      <div className="ac-skel__bar ac-skel__bar--w33" />
      <div className="ac-skel__bar ac-skel__bar--block" />
      <div className="ac-skel__bar ac-skel__bar--w50" />
      <div className="ac-skel__bar ac-skel__bar--w66" />
    </div>
  );
}

function ErrorBanner({ message }: { message: string }) {
  return (
    <div className="ac-error-banner">
      <strong className="ac-error-banner__title">Widget Error</strong>
      {message}
    </div>
  );
}

function AppShell() {
  const [params] = useSearchParams();
  const { component: paramComponent } = useParams<{ component: string }>();
  const comp = params.get("component") || paramComponent || "";
  const isCompare =
    comp === "compare_table" || comp === "concept_compare_table";
  const isOrderWidget = ["order_list", "track_my_orders_form"].includes(comp);

  const appClass = isCompare || isOrderWidget ? "ac-app ac-app--plain" : "ac-app";

  return (
    <div className={appClass}>
      <WidgetView />
    </div>
  );
}

export default function App() {
  return (
    <Routes>
      <Route path="/showcase" element={<WidgetShowcase />} />
      <Route path="/" element={<AppShell />} />
      <Route path="/:component" element={<AppShell />} />
      <Route path="*" element={<AppShell />} />
    </Routes>
  );
}
